import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { MotifProvider } from "./contexts/MotifContext";
import { Toaster } from "sonner";

import Landing from "./pages/Landing";
import MarketingHome from "./pages/MarketingHome";
import GuestApp from "./pages/GuestApp";
import StaffLogin from "./pages/StaffLogin";
import KitchenDash from "./pages/KitchenDash";
import CoordinatorDash from "./pages/CoordinatorDash";
import CoupleDash from "./pages/CoupleDash";
import AdminDash from "./pages/AdminDash";
import EmbroideryStaffDash from "./pages/EmbroideryStaffDash";
import DJDash from "./pages/DJDash";
import PhotographerDash from "./pages/PhotographerDash";
import Projector from "./pages/Projector";
import MCDash from "./pages/MCDash";
import MCShow from "./pages/MCShow";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-motif">
      <div className="text-motif-muted text-sm tracking-luxe" data-testid="auth-loading">Loading…</div>
    </div>
  );
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/vendor" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/riyazandaimee" replace />;
  return children;
}

function GuestProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user || user.role !== "guest") return <Navigate to="/riyazandaimee" replace />;
  return children;
}

// PWA launch (manifest start_url): route the user to their surface.
const LAUNCH_DEST = {
  guest: "/guest", kitchen: "/kitchen", coordinator: "/coordinator", couple: "/couple",
  admin: "/admin", embroidery: "/embroidery", dj: "/dj", mc: "/mc", photographer: "/photographer",
};
function Launch() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/riyazandaimee" replace />;
  return <Navigate to={LAUNCH_DEST[user.role] || "/riyazandaimee"} replace />;
}

// Route role allow-lists hoisted to module scope (stable references, no re-render churn)
const ROLES = {
  kitchen: ["kitchen", "admin", "couple"],
  coordinator: ["coordinator", "admin", "couple"],
  couple: ["couple", "admin"],
  admin: ["admin"],
  embroidery: ["embroidery", "admin", "couple", "coordinator"],
  dj: ["dj", "admin", "couple"],
  mc: ["mc", "admin"],
  photographer: ["photographer", "admin", "couple", "coordinator"],
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <MotifProvider>
          <Toaster position="top-center" richColors closeButton />
          <Routes>
            <Route path="/" element={<MarketingHome />} />
            <Route path="/launch" element={<Launch />} />
            <Route path="/riyazandaimee" element={<Landing />} />
            <Route path="/guest" element={<GuestProtected><GuestApp /></GuestProtected>} />
            <Route path="/vendor" element={<StaffLogin />} />
            {/* Phase 5C: legacy /staff links permanently redirect to /vendor */}
            <Route path="/staff" element={<Navigate to="/vendor" replace />} />
            <Route path="/kitchen" element={<Protected roles={ROLES.kitchen}><KitchenDash /></Protected>} />
            <Route path="/coordinator" element={<Protected roles={ROLES.coordinator}><CoordinatorDash /></Protected>} />
            <Route path="/couple" element={<Protected roles={ROLES.couple}><CoupleDash /></Protected>} />
            <Route path="/admin" element={<Protected roles={ROLES.admin}><AdminDash /></Protected>} />
            <Route path="/embroidery" element={<Protected roles={ROLES.embroidery}><EmbroideryStaffDash /></Protected>} />
            <Route path="/dj" element={<Protected roles={ROLES.dj}><DJDash /></Protected>} />
            <Route path="/mc" element={<Protected roles={ROLES.mc}><MCDash /></Protected>} />
            <Route path="/mc/show" element={<Protected roles={ROLES.mc}><MCShow /></Protected>} />
            <Route path="/photographer" element={<Protected roles={ROLES.photographer}><PhotographerDash /></Protected>} />
            {/* Phase 3B: /bar removed. Any inbound link redirects to the wedding. */}
            <Route path="/bar" element={<Navigate to="/riyazandaimee" replace />} />
            <Route path="/projector/:eventId" element={<Projector />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </MotifProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
